import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Play, Pause, RotateCcw, Users, Shuffle, ShieldCheck,
  KeyRound, ArrowRight, Sparkles, TrendingUp,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────
   A/B Groups "video-like" showcase — 6 scenes × 15s = 90s loop.
   Pure JSX + Framer Motion (no MP4). Matches brutalist palette.
   ──────────────────────────────────────────────────────────────────── */

const BRUT = "border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A]";
const BRUT_LG = "border-2 border-[#0F172A] shadow-[6px_6px_0px_#0F172A]";
const SCENE_MS = 15000;
const TICK_MS = 100;

const scenes = [
  { key: "hook",    title: "Problem",             icon: Users,       accent: "#FCA5A5" },
  { key: "toggle",  title: "Włącz Grupy A/B",     icon: KeyRound,    accent: "#FEF08A" },
  { key: "codes",   title: "Dwa niezależne kody", icon: KeyRound,    accent: "#E0F2FE" },
  { key: "random",  title: "Random Order",         icon: Shuffle,     accent: "#FFEDD5" },
  { key: "balance", title: "Balans A vs B",       icon: TrendingUp,  accent: "#D1FAE5" },
  { key: "cta",     title: "Efekt",               icon: ShieldCheck, accent: "#FEF08A" },
];

export default function ABGroupsShowcase() {
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const rafRef = useRef(null);
  const lastTsRef = useRef(null);

  // rAF-driven progress → smooth on any refresh rate, no drift
  useEffect(() => {
    if (!playing) {
      lastTsRef.current = null;
      return;
    }
    const step = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = ts - lastTsRef.current;
      lastTsRef.current = ts;
      setElapsed((e) => {
        const next = e + dt;
        if (next >= SCENE_MS) {
          setIdx((i) => (i + 1) % scenes.length);
          return 0;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [playing]);

  const jumpTo = (i) => { setIdx(i); setElapsed(0); };
  const restart = () => { setIdx(0); setElapsed(0); setPlaying(true); };
  const progress = elapsed / SCENE_MS;
  const current = scenes[idx];
  const Icon = current.icon;

  const totalSec = 90;
  const currentSec = Math.min(totalSec, Math.floor(idx * (SCENE_MS / 1000) + elapsed / 1000));

  return (
    <section
      className="bg-[#F8FAFC] border-y-2 border-[#0F172A] relative overflow-hidden"
      data-testid="ab-groups-showcase"
    >
      {/* faint diagonal stripes */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #0F172A 0 1px, transparent 1px 22px)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-24">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="inline-block bg-[#0F172A] text-[#FEF08A] px-3 py-1
                             text-[11px] tracking-[0.25em] uppercase font-black">
              Anti-cheat · 90s demo
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl
                           mt-4 leading-tight">
              Grupy A/B: dwie kartkówki,<br />
              <span className="bg-[#FEF08A] px-2">jeden panel</span>.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#334155] max-w-2xl">
              Zobacz, jak w 90 sekund odetniesz ściąganie z sąsiedniej ławki —
              bez zmiany podręcznika, bez papieru, bez extra pracy.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs font-black
                          tracking-[0.15em] uppercase text-[#64748B]">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500
                             animate-pulse" />
            Live demo
          </div>
        </div>

        {/* Player frame */}
        <div className={`${BRUT_LG} bg-white rounded-md overflow-hidden`}>
          {/* Faux browser chrome */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b-2
                          border-[#0F172A] bg-[#F1F5F9]">
            <span className="w-3 h-3 rounded-full bg-[#FCA5A5] border border-[#0F172A]" />
            <span className="w-3 h-3 rounded-full bg-[#FEF08A] border border-[#0F172A]" />
            <span className="w-3 h-3 rounded-full bg-[#86EFAC] border border-[#0F172A]" />
            <div className="ml-3 flex-1 max-w-md bg-white border-2 border-[#0F172A]
                            rounded px-3 py-0.5 text-xs font-mono truncate">
              kartkowka.pl/teacher · <span className="text-[#64748B]">{current.title}</span>
            </div>
            <span className="text-[10px] font-black tracking-[0.2em] uppercase
                             text-[#64748B] tabular-nums">
              {String(currentSec).padStart(2, "0")}s / 90s
            </span>
          </div>

          {/* Scene stage */}
          <div className="relative bg-gradient-to-br from-[#F8FAFC] to-[#E0F2FE]/40
                          min-h-[420px] sm:min-h-[460px] p-6 sm:p-10 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="h-full"
              >
                {current.key === "hook"    && <SceneHook />}
                {current.key === "toggle"  && <SceneToggle />}
                {current.key === "codes"   && <SceneCodes />}
                {current.key === "random"  && <SceneRandom />}
                {current.key === "balance" && <SceneBalance />}
                {current.key === "cta"     && <SceneCTA />}
              </motion.div>
            </AnimatePresence>

            {/* Corner chip: current scene label */}
            <div className="absolute top-4 right-4">
              <div className={`${BRUT} bg-white rounded px-2.5 py-1 flex items-center gap-1.5`}>
                <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                <span className="text-[10px] font-black uppercase tracking-[0.15em]">
                  {String(idx + 1).padStart(2, "0")} · {current.title}
                </span>
              </div>
            </div>
          </div>

          {/* Controls row */}
          <div className="border-t-2 border-[#0F172A] bg-white">
            {/* Progress bar (per-scene) */}
            <div className="h-2 bg-[#F1F5F9] border-b-2 border-[#0F172A] relative">
              <div
                className="h-full bg-[#0F172A] transition-[width] ease-linear"
                style={{ width: `${progress * 100}%`, transitionDuration: `${TICK_MS}ms` }}
              />
            </div>

            <div className="flex items-center justify-between px-4 py-3 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className={`${BRUT} bg-[#FEF08A] rounded w-10 h-10 flex items-center
                              justify-center hover:-translate-y-[2px] transition-transform`}
                  aria-label={playing ? "Pauza" : "Odtwarzaj"}
                  data-testid="showcase-playpause"
                >
                  {playing
                    ? <Pause className="w-4 h-4" strokeWidth={3} />
                    : <Play  className="w-4 h-4" strokeWidth={3} />}
                </button>
                <button
                  onClick={restart}
                  className={`${BRUT} bg-white rounded w-10 h-10 flex items-center
                              justify-center hover:-translate-y-[2px] transition-transform`}
                  aria-label="Od początku"
                  data-testid="showcase-restart"
                >
                  <RotateCcw className="w-4 h-4" strokeWidth={3} />
                </button>
              </div>

              {/* Dot navigation with scene labels */}
              <div className="flex items-center gap-1.5 flex-wrap justify-center">
                {scenes.map((s, i) => (
                  <button
                    key={s.key}
                    onClick={() => jumpTo(i)}
                    className={`
                      group flex items-center gap-1.5 px-2.5 py-1 rounded
                      border-2 border-[#0F172A] text-[10px] font-black uppercase
                      tracking-[0.12em] transition-all
                      ${i === idx
                        ? "bg-[#0F172A] text-white shadow-[2px_2px_0px_#0F172A]"
                        : "bg-white hover:bg-[#F1F5F9]"}
                    `}
                    data-testid={`showcase-dot-${i}`}
                    title={s.title}
                  >
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <span className="hidden sm:inline">{s.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Caption strip below player */}
        <div className="mt-6 flex flex-wrap gap-3 items-center justify-between">
          <div className="text-xs sm:text-sm text-[#64748B] max-w-2xl">
            Wskazówka: kliknij numer sceny, aby przeskoczyć. Wciśnij{" "}
            <kbd className="border border-[#0F172A] px-1.5 rounded bg-white font-mono
                            text-[10px]">Pauzę</kbd> jeśli chcesz się przyjrzeć bliżej.
          </div>
          <Link
            to="/register"
            className={`${BRUT} bg-[#FEF08A] rounded px-4 py-2.5 font-black text-sm
                        inline-flex items-center gap-2 hover:-translate-y-[2px]
                        transition-transform`}
            data-testid="showcase-cta-register"
          >
            Przetestuj za darmo <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Individual scenes — each is a self-contained mini-mockup with its
   own entry animations. Keep them lightweight and legible.
   ──────────────────────────────────────────────────────────────────── */

function SceneHook() {
  const desks = [0, 1, 2, 3, 4];
  return (
    <div className="grid md:grid-cols-2 gap-8 items-center h-full">
      <div>
        <div className="text-[11px] tracking-[0.25em] uppercase font-black
                        text-[#B91C1C]">Bez Grup A/B</div>
        <h3 className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight">
          Dwoje uczniów obok siebie<br />
          <span className="line-through decoration-[#B91C1C] decoration-4">
            widzi ten sam ekran
          </span>.
        </h3>
        <p className="mt-3 text-[#334155] text-sm sm:text-base">
          Klasyczna kartkówka = jeden kod = te same pytania w tej samej kolejności.
          Ściąga sama się prosi.
        </p>
        <ul className="mt-4 space-y-1.5 text-sm">
          {["To samo pytanie #1", "Ta sama odpowiedź poprawna", "Kacper spisuje od Oli"].map(
            (t, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B91C1C]" />
                <span>{t}</span>
              </li>
            )
          )}
        </ul>
      </div>

      <div className="grid grid-cols-5 gap-2 sm:gap-3">
        {desks.map((i) => {
          const cheater = i === 1 || i === 2;
          return (
            <motion.div
              key={i}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15 + i * 0.1, duration: 0.4 }}
              className={`${BRUT} rounded p-2 text-center ${
                cheater ? "bg-[#FEE2E2]" : "bg-white"
              }`}
            >
              <div className="text-2xl">{cheater ? "👀" : "🧑‍🎓"}</div>
              <div className="text-[9px] font-black uppercase tracking-wider mt-1">
                {["Ola","Kacper","Piotr","Zosia","Kuba"][i]}
              </div>
              <div className="mt-1 h-0.5 bg-[#0F172A]/20" />
              <div className="text-[9px] font-mono mt-1">
                Q1: {cheater ? "H₂SO₄" : "?"}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function SceneToggle() {
  return (
    <div className="grid md:grid-cols-5 gap-6 items-center h-full">
      <div className="md:col-span-2">
        <div className="text-[11px] tracking-[0.25em] uppercase font-black text-[#0F172A]">
          Krok 1
        </div>
        <h3 className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight">
          Włącz jeden toggle<br />
          w oknie „Nowa sesja".
        </h3>
        <p className="mt-3 text-[#334155] text-sm sm:text-base">
          System sam podzieli pytania na dwie rozłączne pule i wygeneruje
          drugi kod. Nic więcej nie musisz robić.
        </p>
      </div>

      {/* Faux modal */}
      <div className="md:col-span-3">
        <motion.div
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className={`${BRUT_LG} bg-white rounded-md p-5 max-w-md ml-auto`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-black text-lg">Nowa sesja</div>
            <span className="text-xs font-mono text-[#64748B]">chem-01</span>
          </div>

          <div className="space-y-3 text-sm">
            <FauxField label="Klasa" value="1A · LO im. M. Curie" />
            <FauxField label="Zakres" value="Mol i stechiometria" />
            <FauxField label="Liczba pytań" value="10" />

            {/* The animated toggle */}
            <motion.div
              className={`${BRUT} bg-[#FEF08A] rounded p-3 flex items-center justify-between`}
              initial={{ backgroundColor: "#FFFFFF" }}
              animate={{ backgroundColor: "#FEF08A" }}
              transition={{ delay: 1.4, duration: 0.3 }}
            >
              <div>
                <div className="font-black text-sm">Grupy A/B</div>
                <div className="text-[11px] text-[#334155]">Rozdziel pytania na dwie grupy</div>
              </div>
              <motion.div
                initial={{ x: 0 }}
                animate={{ x: 20 }}
                transition={{ delay: 1.4, duration: 0.35, type: "spring", stiffness: 260 }}
                className="w-11 h-6 border-2 border-[#0F172A] rounded-full bg-white relative"
              >
                <motion.span
                  className="absolute top-0.5 left-0.5 w-4 h-4 bg-[#0F172A] rounded-full"
                  initial={{ x: 0 }}
                  animate={{ x: 18 }}
                  transition={{ delay: 1.4, duration: 0.35, type: "spring", stiffness: 260 }}
                />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.9, duration: 0.35 }}
              className={`${BRUT} bg-[#D1FAE5] rounded p-2.5 text-[11px] font-bold`}
            >
              <Sparkles className="inline w-3.5 h-3.5 mr-1" strokeWidth={3} />
              Wygeneruję 2 kody i podzielę pytania 5/5.
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function FauxField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-[0.15em] text-[#64748B] mb-0.5">
        {label}
      </div>
      <div className={`${BRUT} bg-white rounded px-2.5 py-1.5 text-sm`}>{value}</div>
    </div>
  );
}

function SceneCodes() {
  return (
    <div className="h-full flex flex-col justify-center">
      <div className="text-[11px] tracking-[0.25em] uppercase font-black text-[#0F172A] mb-2">
        Krok 2
      </div>
      <h3 className="font-display font-black text-2xl sm:text-3xl leading-tight max-w-xl">
        Rozdaj dwa kody — każdy uczeń <br />dostaje inny zestaw pytań.
      </h3>

      <div className="grid sm:grid-cols-2 gap-5 mt-6">
        {[
          { label: "Grupa A", code: "X4KL2M", bg: "#FEF08A", pool: ["Ola", "Piotr", "Kuba"] },
          { label: "Grupa B", code: "7NRP3Y", bg: "#E0F2FE", pool: ["Kacper", "Zosia", "Ada"] },
        ].map((g, i) => (
          <motion.div
            key={g.label}
            initial={{ x: i === 0 ? -30 : 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 + i * 0.2, duration: 0.5 }}
            className={`${BRUT_LG} rounded-md p-4`}
            style={{ backgroundColor: g.bg }}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                {g.label}
              </span>
              <span className="text-[10px] font-mono bg-white/70 border border-[#0F172A]
                               px-1.5 rounded">10 pytań</span>
            </div>
            <div className="font-mono font-black text-3xl sm:text-4xl tracking-[0.15em]
                            mt-3 text-center">
              {g.code}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5 justify-center">
              {g.pool.map((n) => (
                <span key={n} className={`${BRUT} bg-white rounded px-2 py-0.5
                                          text-[10px] font-black`}>
                  {n}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.4 }}
        className="mt-5 text-xs sm:text-sm text-[#334155] flex items-center gap-2"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#0F172A]" />
        Pule pytań są <b className="mx-1">rozłączne</b> — Ola i Kacper nie mogą
        porównać odpowiedzi, bo widzą różne zadania.
      </motion.div>
    </div>
  );
}

function SceneRandom() {
  const questionsA = ["Wzór kwasu siarkowego?", "Masa molowa H₂O?", "Typ reakcji: Na + Cl₂ →?"];
  return (
    <div className="grid md:grid-cols-2 gap-6 items-center h-full">
      <div>
        <div className="text-[11px] tracking-[0.25em] uppercase font-black text-[#0F172A]">
          Bonus
        </div>
        <h3 className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight">
          Nawet w tej samej grupie —<br />
          <span className="bg-[#FFEDD5] px-2">inna kolejność</span> pytań.
        </h3>
        <p className="mt-3 text-[#334155] text-sm sm:text-base">
          Włącz <b>Losowa kolejność</b> obok toggla Grup A/B. Ola i Piotr są w tej samej
          grupie, ale każdy widzi pytania w innej sekwencji.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          { name: "Ola",   order: [0, 1, 2], bg: "#FEF08A" },
          { name: "Piotr", order: [2, 0, 1], bg: "#FEF08A" },
        ].map((s, si) => (
          <motion.div
            key={s.name}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 + si * 0.15, duration: 0.4 }}
            className={`${BRUT} rounded p-3`}
            style={{ backgroundColor: s.bg }}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="w-7 h-7 rounded-full bg-white border-2 border-[#0F172A]
                               flex items-center justify-center text-sm">
                {s.name[0]}
              </span>
              <span className="text-xs font-black uppercase tracking-wider">{s.name}</span>
              <span className="ml-auto text-[9px] font-black bg-white border border-[#0F172A]
                               px-1.5 rounded">Grupa A</span>
            </div>
            <ol className="space-y-1.5">
              {s.order.map((qi, i) => (
                <motion.li
                  key={i}
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 + si * 0.15 + i * 0.15, duration: 0.35 }}
                  className={`${BRUT} bg-white rounded px-2 py-1.5 text-[11px] font-mono
                              flex items-center gap-2`}
                >
                  <span className="w-4 h-4 bg-[#0F172A] text-white text-[9px] font-black
                                   flex items-center justify-center rounded-sm">
                    {i + 1}
                  </span>
                  <span className="truncate">{questionsA[qi]}</span>
                </motion.li>
              ))}
            </ol>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function SceneBalance() {
  return (
    <div className="h-full flex flex-col justify-center">
      <div className="text-[11px] tracking-[0.25em] uppercase font-black text-[#0F172A] mb-2">
        Krok 3 · Panel na żywo
      </div>
      <h3 className="font-display font-black text-2xl sm:text-3xl leading-tight max-w-xl">
        Widzisz obie grupy obok siebie —<br />
        wiesz, czy podział był fair.
      </h3>

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {[
          { label: "Grupa A", students: 12, avg: 78, bg: "#FEF08A", bar: "#F59E0B" },
          { label: "Grupa B", students: 11, avg: 76, bg: "#E0F2FE", bar: "#0EA5E9" },
        ].map((g, i) => (
          <motion.div
            key={g.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.15, duration: 0.4 }}
            className={`${BRUT} rounded-md p-4`}
            style={{ backgroundColor: g.bg }}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-black uppercase tracking-[0.15em]">
                {g.label}
              </span>
              <span className="text-[10px] font-mono">{g.students} uczniów</span>
            </div>
            <div className="mt-2 font-display font-black text-4xl">
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 + i * 0.15 }}
              >
                {g.avg}%
              </motion.span>
              <span className="text-sm font-bold ml-2 text-[#334155]">średnia</span>
            </div>
            <div className="mt-3 h-3 bg-white border-2 border-[#0F172A] rounded-sm overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${g.avg}%` }}
                transition={{ delay: 0.6 + i * 0.15, duration: 0.9, ease: "easeOut" }}
                className="h-full"
                style={{ backgroundColor: g.bar }}
              />
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.4 }}
        className={`${BRUT} bg-[#D1FAE5] rounded mt-5 p-3 flex items-center gap-3`}
      >
        <span className="w-8 h-8 rounded-full bg-[#059669] text-white flex items-center
                         justify-center font-black">✓</span>
        <div className="text-sm">
          <div className="font-black">Balans OK — różnica 2 pp.</div>
          <div className="text-[11px] text-[#334155]">
            Alert włączy się dopiero powyżej 8 pp różnicy.
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SceneCTA() {
  return (
    <div className="h-full flex flex-col justify-center items-center text-center">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className={`${BRUT_LG} bg-[#FEF08A] rounded-full w-20 h-20 flex items-center
                    justify-center mb-5`}
      >
        <ShieldCheck className="w-10 h-10" strokeWidth={2.5} />
      </motion.div>
      <h3 className="font-display font-black text-3xl sm:text-4xl leading-tight max-w-2xl">
        Ściąganie? <span className="bg-[#0F172A] text-[#FEF08A] px-2">Niemożliwe.</span>
      </h3>
      <p className="mt-3 text-[#334155] text-base max-w-lg">
        Dwie pule pytań, losowa kolejność i live-balans — wszystko w tej samej
        kartkówce, w tym samym czasie.
      </p>

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        {[
          "2 rozłączne kody",
          "Random order",
          "Balans A vs B",
          "Zero papieru",
        ].map((t, i) => (
          <motion.span
            key={t}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className={`${BRUT} bg-white rounded px-3 py-1.5 text-xs font-black
                        uppercase tracking-[0.12em]`}
          >
            ✓ {t}
          </motion.span>
        ))}
      </div>

      <Link
        to="/register"
        className={`${BRUT_LG} bg-[#0F172A] text-[#FEF08A] rounded mt-7 px-6 py-3
                    font-black inline-flex items-center gap-2
                    hover:-translate-y-[3px] transition-transform`}
        data-testid="showcase-final-cta"
      >
        Załóż konto i uruchom pierwszą Grupę A/B
        <ArrowRight className="w-4 h-4" strokeWidth={3} />
      </Link>
    </div>
  );
}
